import {
  Alert,
  AlertDescription,
  Box,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/toast";
import {
  CloudDownload as CloudDownloadIcon,
  Folder as FolderIcon,
} from "@mui/icons-material";
import {
  ChangeEvent,
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { graphql } from "react-relay";

import { UrlMappingCsvUploadModal_Mutation } from "~/src/__relay_artifacts__/UrlMappingCsvUploadModal_Mutation.graphql";
import {
  GhostButton,
  SolidButton,
  SymbolOutlineButton,
} from "~/src/components/common/Button";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormControlGroup } from "~/src/components/common/forms/FormControlGroup";
import { Modal } from "~/src/components/common/Modal";
import { useMutationCommit } from "~/src/lib/react-relay";
import { summarizeImportRowFailureMessages } from "~/src/lib/utils/fomatUnknownError";

import { UrlMappingCsvImportPreview } from "../../UrlMappingImport/UrlMappingCsvImportPreview";
import {
  IMPORT_MAX_URL_MAPPINGS,
  ParsedUrlMappingImportRow,
  downloadImportTemplate,
  parseCsvToImportRows,
  rowToImportInput,
} from "../../UrlMappingImport/urlMappingCsvUtils";

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  siteSlug: string;
  onImportSuccess: () => void;
};

type Step = "upload" | "preview";

const mutation = graphql`
  mutation UrlMappingCsvUploadModal_Mutation($input: ImportUrlMappingsInput!) {
    importUrlMappings(input: $input) {
      results {
        rowIndex
        success
        errors {
          field
          message
        }
        urlMapping {
          id
        }
      }
    }
  }
`;

const resetFileInput = (fileInputRef: RefObject<HTMLInputElement>) => {
  if (fileInputRef.current) {
    fileInputRef.current.value = "";
  }
};

export const UrlMappingCsvUploadModal: FC<Props> = ({
  isOpen,
  onClose,
  siteSlug,
  onImportSuccess,
}) => {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>("upload");
  const [fileName, setFileName] = useState("");
  const [parsedRows, setParsedRows] = useState<ParsedUrlMappingImportRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const importMutation =
    useMutationCommit<UrlMappingCsvUploadModal_Mutation>(mutation);

  const resetState = useCallback(() => {
    setStep("upload");
    setFileName("");
    setParsedRows([]);
    setParseErrors([]);
    setCsvErrors([]);
    setIsParsing(false);
    setIsImporting(false);
    resetFileInput(fileInputRef);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    resetState();
  }, [isOpen, resetState]);

  const rowValidationErrors = useMemo(
    () => parsedRows.flatMap((parsedRow) => parsedRow.errors),
    [parsedRows]
  );

  const canImport = useMemo(
    () =>
      parsedRows.length > 0 &&
      parseErrors.length === 0 &&
      rowValidationErrors.length === 0,
    [parseErrors.length, parsedRows.length, rowValidationErrors.length]
  );

  const handleTemplateDownload = useCallback(() => {
    downloadImportTemplate();
  }, []);

  const handleClickFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleBackToUpload = useCallback(() => {
    setStep("upload");
    setParsedRows([]);
    setParseErrors([]);
    setCsvErrors([]);
    setFileName("");
    resetFileInput(fileInputRef);
  }, []);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setFileName(file.name);
      setCsvErrors([]);
      setParseErrors([]);
      setParsedRows([]);
      setIsParsing(true);

      try {
        const { parsedRows: rows, errors } = await parseCsvToImportRows(file);
        const validationErrors = rows.flatMap((row) => row.errors);

        if (rows.length === 0) {
          setCsvErrors(
            [...errors, ...validationErrors].length > 0
              ? [...errors, ...validationErrors]
              : ["インポート対象の行がありません"]
          );
          return;
        }

        setParsedRows(rows);
        setParseErrors(errors);
        setStep("preview");
      } catch {
        setCsvErrors(["CSVの読み込みに失敗しました"]);
      } finally {
        setIsParsing(false);
        resetFileInput(fileInputRef);
      }
    },
    []
  );

  const handleImport = useCallback(async () => {
    if (!canImport) return;

    setCsvErrors([]);
    setIsImporting(true);

    try {
      const { importUrlMappings } = await importMutation({
        variables: {
          input: {
            siteSlug,
            rows: parsedRows.map((parsedRow) =>
              rowToImportInput(parsedRow.row)
            ),
          },
        },
      });

      const results = importUrlMappings?.results ?? [];
      const successCount = results.filter((result) => result.success).length;
      const failed = results.filter((result) => !result.success);

      if (successCount > 0) {
        toast({
          title: `${successCount}件のURLマッピングをインポートしました`,
          status: "success",
        });
        onImportSuccess();
      }

      if (failed.length > 0) {
        const failureDetail = summarizeImportRowFailureMessages(
          failed.map((result) => ({
            rowIndex: result.rowIndex ?? 0,
            errors: result.errors ?? [],
          }))
        );
        setCsvErrors(
          failureDetail ? [failureDetail] : ["インポートに失敗した行があります"]
        );
      } else {
        onClose();
      }
    } catch {
      setCsvErrors(["インポートに失敗しました"]);
    } finally {
      setIsImporting(false);
    }
  }, [
    canImport,
    importMutation,
    onClose,
    onImportSuccess,
    parsedRows,
    siteSlug,
    toast,
  ]);

  const modalTitle =
    step === "preview" ? "インポート内容の確認" : "CSVアップロード";
  const modalWidth = step === "preview" ? "960px" : "640px";

  return (
    <Modal
      title={modalTitle}
      isOpen={isOpen}
      onClose={onClose}
      width={modalWidth}
    >
      {step === "upload" ? (
        <FormControlGroup>
          <FormControl as="div" label="テンプレート">
            <SymbolOutlineButton
              icon={<CloudDownloadIcon />}
              onClick={handleTemplateDownload}
            >
              CSVテンプレートをダウンロード
            </SymbolOutlineButton>
            <Text fontSize="11px" color="#718096" mt="4px">
              サンプル行付きのCSVをダウンロードできます。1行1ルールです。 exact
              で複数ソースURLは source_url 列に | 区切り、複数の p 値は
              condition_query_param_values に | 区切りで指定します。
            </Text>
          </FormControl>
          <FormControl as="div" label="CSVファイル">
            <HStack alignItems="center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={isParsing || isImporting}
                // eslint-disable-next-line react/forbid-dom-props
                style={{ display: "none" }}
              />
              <SymbolOutlineButton
                icon={<FolderIcon />}
                onClick={handleClickFileInput}
                disabled={isParsing || isImporting}
              >
                {isParsing ? "読み込み中..." : "ファイルを選択"}
              </SymbolOutlineButton>
              {fileName ? (
                <Text fontSize="13px" color="#4A5568" ml="8px">
                  {fileName}
                </Text>
              ) : null}
            </HStack>
            <Text fontSize="12px" color="#718096" mt="4px">
              ※ ファイル選択後、内容を確認してからインポートできます（最大
              {IMPORT_MAX_URL_MAPPINGS}件）
            </Text>
          </FormControl>
        </FormControlGroup>
      ) : (
        <Box>
          <Text fontSize="13px" color="#4A5568" mb="12px">
            {fileName} — {parsedRows.length}件
          </Text>
          {parseErrors.length > 0 ? (
            <Alert
              status="warning"
              variant="left-accent"
              mb="12px"
              borderRadius="4px"
            >
              <VStack alignItems="flex-start" spacing="4px">
                {parseErrors.map((error, index) => (
                  <AlertDescription key={index} fontSize="13px">
                    {error}
                  </AlertDescription>
                ))}
              </VStack>
            </Alert>
          ) : null}
          <UrlMappingCsvImportPreview parsedRows={parsedRows} />
          {!canImport ? (
            <Text fontSize="12px" color="#E53E3E" mt="8px">
              エラーがある行を修正してから、再度CSVを選択してください。
            </Text>
          ) : (
            <Text fontSize="12px" color="#718096" mt="8px">
              内容を確認し、「インポート実行」を押すと登録されます。
            </Text>
          )}
        </Box>
      )}

      {csvErrors.length > 0 ? (
        <Alert
          status="error"
          variant="left-accent"
          mb="16px"
          borderRadius="4px"
        >
          <VStack alignItems="flex-start" spacing="4px">
            {csvErrors.map((err, i) => (
              <AlertDescription key={i} fontSize="13px">
                {err}
              </AlertDescription>
            ))}
          </VStack>
        </Alert>
      ) : null}

      <HStack spacing="8px" justifyContent="flex-end">
        {step === "preview" ? (
          <GhostButton onClick={handleBackToUpload} disabled={isImporting}>
            戻る
          </GhostButton>
        ) : null}
        <GhostButton onClick={onClose} disabled={isImporting}>
          閉じる
        </GhostButton>
        {step === "preview" ? (
          <SolidButton
            onClick={handleImport}
            isDisabled={!canImport}
            isLoading={isImporting}
          >
            インポート実行
          </SolidButton>
        ) : null}
      </HStack>
    </Modal>
  );
};
